import { TextField, TextFieldProps } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface IsolatedTextFieldProps extends TextFieldProps<'standard'> {
  onEditFinished: (value: string) => void;
  value?: string;
  selectAllOnFocus?: boolean;
  resetOnEscape?: boolean;
  allowEmptySubmit?: boolean;
}

const DefaultProps: Partial<IsolatedTextFieldProps> = {
  selectAllOnFocus: true,
  resetOnEscape: true,
  allowEmptySubmit: true,
};

type TextfieldElementType = HTMLInputElement | HTMLTextAreaElement;

// Textfield that isolates changes during editing so the containing component can avoid
// unnecessary state updates while someone is typing a new value. Instead the change will be
// sent to the containing component when the user presses enter or it loses focus.

export const IsolatedTextField = (props: IsolatedTextFieldProps) => {
  const propsWithDefaults = { ...DefaultProps, ...props };

  const { onEditFinished, selectAllOnFocus, allowEmptySubmit, resetOnEscape, value: inValue, ...textFieldProps } = propsWithDefaults;

  const [value, setValue] = useState<string>(inValue || '');

  useEffect(() => {
    setValue(props.value || '');
  }, [props.value]);

  const handleSubmit = () => {
    const toSubmit = value.trim();
    if (toSubmit.length > 0 || allowEmptySubmit) onEditFinished(value);
    else {
      setValue(inValue || '');
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const key = e.key.toLowerCase();

    switch (key) {
      case 'escape':
        if (resetOnEscape) {
          setValue(inValue || '');
          e.currentTarget.blur();
        }
        break;

      case 'enter':
        handleSubmit();
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.blur();

        break;
    }
  };

  const onFocus = (e: React.FocusEvent<TextfieldElementType>) => {
    if (selectAllOnFocus) {
      e.currentTarget.select();
    }
  };

  const onBlur = () => {
    handleSubmit();
  };

  const onChange = (e: React.ChangeEvent<TextfieldElementType>) => {
    setValue(e.target.value);
  };

  return (
    <TextField
      {...textFieldProps}
      value={value}
      onChange={onChange}
      slotProps={{
        htmlInput: {
          onFocus,
          onBlur,
          onKeyDown,
        },
      }}
    />
  );
};
